/**
 * REST client for Playbook Live persona authoring.
 *
 * Everything here is plain REST. The only streaming in authoring is the
 * admin's dictation, which goes straight from the browser to the Deepgram
 * proxy via `onboardingAudioCaptureService` and never passes through these
 * calls — by the time a brief arrives here it is already text.
 *
 * Synthesis is split into two requests on purpose. Together they are three
 * sequential model round-trips (~40s), which is past a sane HTTP timeout and a
 * dead spinner; split, the admin reads and edits the persona while the agenda
 * is still being chosen.
 */

import { apiService } from './apiService';

/** How the customer sounds. How hard it is to convince is the track's difficulty. */
export type CustomerTone = 'neutral' | 'frustrated' | 'curious' | 'aggressive';

export interface DecisionContext {
  budgetRange: string;
  hasAuthority: boolean;
  timeline: string;
  otherStakeholders: string[];
  alternativesBeingEvaluated: string[];
}

export interface PersonaDraft {
  aiCustomerRole: string;
  accountProfile?: string;
  personality?: CustomerTone;
  talkativeness?: 'terse' | 'normal' | 'chatty';
  buyingObjective: string;
  currentSituation?: string;
  triggerEvent?: string;
  painPoints?: string[];
  focusAreas?: string[];
  decisionContext?: DecisionContext;
  openingLine?: string;
  advancedTalkingPoints?: string[];
  endConditions?: string;
  hardRules?: string[];
  additionalInstructions?: string;
  suggestedItemCount?: number;
  /** What the model inferred that the brief did not say — shown so the admin
   *  can catch an invention they disagree with before it becomes the customer. */
  interpretationNotes?: string;
}

export interface AgendaItemDraft {
  itemId: string;
  contentId: string;
  type: 'objection' | 'faq' | 'landmine' | 'competitor';
  category: string;
  preview: string;
  reason: string;
  order: number;
  mustRaise: boolean;
  /** Display-only text (question / trigger / name) in the same shape the
   *  builder's knowledge-base item list renders. Never carries the answer. */
  contentSnapshot?: { question?: string; questions?: string[]; trigger?: string; triggers?: string[]; name?: string };
}

export interface AgendaResponse {
  agenda: AgendaItemDraft[];
  agendaRationale: string;
  candidateCount: number;
  warning: string;
}

/** apiService flattens the HTTP body into { success, data, message }. */
const unwrap = <T,>(response: any, what: string): T => {
  if (!response?.success) {
    throw new Error(response?.message || `We could not ${what}. Try again.`);
  }
  return response.data as T;
};

export const draftPersona = async (
  brief: string,
  targetDurationMinutes: number
): Promise<PersonaDraft> => {
  const response = await apiService.post('/training/playbook/persona/draft', {
    brief,
    targetDurationMinutes,
  });
  return unwrap<{ persona: PersonaDraft }>(response, 'build the customer').persona;
};

/**
 * One example description of an AI customer, written by the backend from this
 * tenant's company details. Throws the API error as-is, so a caller can tell
 * `COMPANY_CONTEXT_INCOMPLETE` (409) from a model failure.
 */
export const draftPersonaExample = async (): Promise<string> => {
  const response = await apiService.post('/training/playbook/persona/example');
  return unwrap<{ example: string }>(response, 'write an example')?.example || '';
};

export const draftAgenda = async (
  persona: PersonaDraft,
  itemCount?: number
): Promise<AgendaResponse> => {
  const response = await apiService.post('/training/playbook/persona/agenda', {
    persona,
    itemCount,
  });
  return unwrap<AgendaResponse>(response, 'choose the concerns');
};

/**
 * Choose the agenda straight from the admin's description, so it can run in
 * parallel with persona synthesis instead of waiting ~12s for it.
 */
export const draftAgendaFromBrief = async (
  brief: string,
  targetDurationMinutes: number
): Promise<AgendaResponse> => {
  const response = await apiService.post('/training/playbook/persona/agenda', {
    brief,
    targetDurationMinutes,
  });
  return unwrap<AgendaResponse>(response, 'choose the concerns');
};

export const previewInstructions = async (
  persona: PersonaDraft,
  difficulty?: string
): Promise<{ instructions: string; approxTokens: number; note: string }> => {
  const response = await apiService.post('/training/playbook/persona/preview', { persona, difficulty });
  return unwrap(response, 'render the prompt');
};

export interface ResolvedAgendaItem {
  contentId: string;
  type: AgendaItemDraft['type'];
  contentSnapshot?: AgendaItemDraft['contentSnapshot'];
}

/** Display text for saved agenda items, so a reopened track shows real KB content. */
export const resolveAgendaItems = async (
  items: Array<{ contentId: string; type: AgendaItemDraft['type'] }>
): Promise<ResolvedAgendaItem[]> => {
  const response = await apiService.post('/training/playbook/persona/resolve-items', { items });
  return unwrap<{ items: ResolvedAgendaItem[] }>(response, 'load the knowledge base items').items || [];
};

export interface KbInventory {
  inventory: Record<string, Array<{ category: string; count: number }>>;
  totals: Record<string, number>;
}

export const fetchKbInventory = async (): Promise<KbInventory> => {
  const response = await apiService.get('/training/playbook/kb-inventory');
  return unwrap<KbInventory>(response, 'read the knowledge base');
};

export interface ReadinessBlocker {
  code: 'COMPANY_CONTEXT_INCOMPLETE' | 'NO_PLAYBOOKS';
  title: string;
  message: string;
  missing: Array<{ key: string; label: string; why: string }>;
  action: { label: string; path: string };
}

export interface PlaybookReadiness {
  ready: boolean;
  blockers: ReadinessBlocker[];
  recommended: Array<{ key: string; label: string; why: string }>;
  contextFieldsComplete: number;
  contextFieldsTotal: number;
  playbookCount: number;
}

/**
 * Whether a playbook track can be built at all.
 *
 * Checked before the authoring step is shown, so an admin learns what is
 * missing before describing a customer rather than after.
 */
export const fetchReadiness = async (): Promise<PlaybookReadiness> => {
  const response = await apiService.get('/training/playbook/readiness');
  return unwrap<PlaybookReadiness>(response, 'check your setup');
};
